import { useContext, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { SwapMainAppContext } from '../../../../context/SwapMainAppContext';
import { PRISMAX_DATA_PIPELINE_URL } from '../../../../configs/config';

import sharedStyles from './Modals.module.css';
import styles from './EarningsModal.module.css';

function EarningsModal({ onClose, showBackButton = false }) {
    const { gatewayToken } = useContext(SwapMainAppContext);
    const token = gatewayToken || localStorage.getItem('gatewayToken');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/qa/get-episode-earnings`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => res.json())
            .then((json) => {
                if (json.success) {
                    setData(json.data);
                } else {
                    setError(json.msg || 'Failed to load earnings.');
                }
            })
            .catch(() => setError('Failed to load earnings.'))
            .finally(() => setLoading(false));
    }, []);

    function formatDate(isoString) {
        if (!isoString) return '—';

        const date = new Date(isoString);
        const now = new Date();

        // Create new Date objects set to midnight local time for accurate calendar day comparison
        const localDateMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const localNowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        const diffTime = localNowMidnight - localDateMidnight;
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    const modal = (
        <div className={sharedStyles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className={sharedStyles.shell} style={{ maxWidth: '520px' }} role="dialog" aria-modal="true" aria-labelledby="earnings-title">
                {/* Header */}
                <div className={sharedStyles.head}>
                    <div className={sharedStyles.headInner}>
                        {showBackButton && (
                            <button className={sharedStyles.backBtn} onClick={onClose} aria-label="Go back">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Back
                            </button>
                        )}
                        <div className={sharedStyles.headLeft}>
                            <span className={sharedStyles.eyebrow}>Review &amp; Earn</span>
                            <span className={sharedStyles.titleTxt} id="earnings-title">My earnings</span>
                        </div>
                    </div>
                    <button className={sharedStyles.closeBtn} onClick={onClose} aria-label="Close">
                        &#215;
                    </button>
                </div>

                {/* Body */}
                <div className={styles.body}>
                    {loading && (
                        <div className={styles.stateMsg}>Loading&hellip;</div>
                    )}
                    {!loading && error && (
                        <div className={styles.stateMsg}>{error}</div>
                    )}
                    {!loading && data && (
                        <>
                            {/* Summary chips */}
                            <div className={styles.chips}>
                                <div className={styles.chip}>
                                    <div className={styles.chipLabel}>Total points</div>
                                    <div className={`${styles.chipValue} ${styles.chipValueBlue}`}>
                                        {(data.total_points || 0).toLocaleString()}
                                    </div>
                                </div>
                                <div className={styles.chip}>
                                    <div className={styles.chipLabel}>Pending</div>
                                    <div className={`${styles.chipValue} ${styles.chipValueAmber}`}>
                                        {(data.pending_points || 0).toLocaleString()}
                                    </div>
                                </div>
                                <div className={styles.chip}>
                                    <div className={styles.chipLabel}>Episodes scored</div>
                                    <div className={styles.chipValue}>
                                        {(data.episodes_scored || 0).toLocaleString()}
                                    </div>
                                </div>
                            </div>

                            {/* Episode log */}
                            {Array.isArray(data.episodes) && data.episodes.length > 0 
                            ? (
                                <div className={styles.log}>
                                    <div className={styles.logHeader}>
                                        <span className={`${styles.logCol} ${styles.logColEp}`}>Episode</span>
                                        <span className={`${styles.logCol} ${styles.logColDate}`}>Submitted</span>
                                        <span className={`${styles.logCol} ${styles.logColPts}`}>Points</span>
                                    </div>
                                    <div className={styles.logBody}>
                                        {(data.episodes || []).map((ep) => (
                                            <div key={ep.episode_id} className={styles.logRow}>
                                                <span className={styles.logEp}>
                                                    #{ep.episode_id}
                                                    {ep.scenario && (
                                                        <span className={styles.logTag}>{ep.scenario}</span>
                                                    )}
                                                </span>
                                                <span className={styles.logDate}>{formatDate(ep.reviewed_at)}</span>
                                                <span className={`${styles.logPts} ${
                                                    ep.status === 'granted' ? styles.logPtsGranted
                                                    : ep.status === 'pending' ? styles.logPtsPending
                                                    : styles.logPtsMissed
                                                }`}>
                                                    {ep.status === 'missed' ? '—' : `+${ep.points}`}
                                                    {ep.status === 'pending' && (
                                                        <span className={styles.logPtsStatus}>pending</span>
                                                    )}
                                                </span>
                                            </div>
                                        )
                                        )}
                                    </div>
                            </div>)
                            : (
                                <div className={styles.stateMsg}>
                                    No reviews yet. Your submitted reviews will appear here.
                                </div>
                            )}
  

                            <div className={styles.footNote}>
                                Pending points are awarded once a consensus score is reached
                                &nbsp;&middot;&nbsp;
                                — means your score fell outside &plusmn;15% or your pass/fail checks did not match
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
}

export default EarningsModal;
